import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Platform, PlatformMetrics } from '@/types/platform';
import { Link as LinkIcon, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MetricCard } from '../common/MetricCard';
import { getPlatformLogo } from '@/utils/platformLogos';

interface PlatformKpiProps {
  platforms: Platform[];
  metrics: PlatformMetrics[];
  isLoading: boolean;
}

export function PlatformKpi({ platforms, metrics, isLoading }: PlatformKpiProps) {
  useLanguage();
  if (isLoading) {
    return (
      <MetricCard
        label={translateInterfaceText("Plataforma")}
        value=""
        description={translateInterfaceText("Plataforma de origem das conversões registradas. Clique para ver detalhes de cada plataforma.")}
        className="animate-pulse"
        icon={Layers}
      />
    );
  }
  
  const activePlatforms = platforms.filter(p => p.status === 'active');
  const primary = platforms.find(p => p.primary) ?? (activePlatforms.length === 1 ? activePlatforms[0] : null);
  
  // Estado vazio
  if (activePlatforms.length === 0) {
    return (
      <MetricCard
        label={translateInterfaceText("Plataforma")}
        description={translateInterfaceText("Plataforma de origem das conversões registradas. Clique para ver detalhes de cada plataforma.")}
        icon={Layers}
        value={
          <Link
            to="/tracking"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
              <LinkIcon className="h-3 w-3 text-muted-foreground" />
            </div>
            <span className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              {translateInterfaceText("Sem plataforma")}</span>
          </Link>
        }
        inlineActions={true}
        color=""
      />
    );
  }
  
  // Estado com múltiplas plataformas
  if (activePlatforms.length > 1 && !primary) {
    return (
      <MetricCard
        label={translateInterfaceText("Plataforma")}
        description={translateInterfaceText("Plataforma de origem das conversões registradas. Clique para ver detalhes de cada plataforma.")}
        icon={Layers}
        value={
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
              {activePlatforms.slice(0, 3).map((platform) => (
                <img 
                  key={platform.id}
                  src={platform.logo_url} 
                  alt={platform.name}
                  className="h-7 w-auto object-contain"
                  style={{ maxWidth: '80px' }}
                />
              ))}
            </div>
            {activePlatforms.length > 3 && (
              <span className="text-xs text-muted-foreground">
                +{activePlatforms.length - 3}
              </span>
            )}
          </div>
        }
        inlineActions={true}
        color=""
      />
    );
  }
  
  // Estado normal - 1 plataforma ou primária definida
  const displayPlatform = primary;
  const logoUrl = displayPlatform?.logo_url || getPlatformLogo(displayPlatform?.name || '');

  return (
    <MetricCard
      label={translateInterfaceText("Plataforma")}
      description={translateInterfaceText("Plataforma de origem das conversões registradas. Clique para ver detalhes de cada plataforma.")}
      icon={Layers}
      value={
        <img
          src={logoUrl}
          alt={displayPlatform?.name}
          className="h-7 w-auto object-contain"
          style={{ maxWidth: '120px' }}
        />
      }
      inlineActions={true}
      color=""
    />
  );
}
